import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import { I18N_KEYS } from '@/language/keys';
import { TEXT_ITERATOR_ID } from '@/components/Nodes/packUnpack.consts';
import { getListSelectorIteratorMaxOptions } from '@/utils/listNode';
import { log } from '@/logger/logger';

interface CsvDialogState {
  open: boolean;
  columns: string[];
  file: File | null;
  dropPosition: { x: number; y: number };
}

interface AddNodeParams {
  action: {
    type: string;
    id: string;
    initialData: { list: string[]; name?: string };
  };
  dropX: number;
  dropY: number;
}

interface UseCsvDropParams {
  addNewNode: (params: AddNodeParams) => void;
  updateSnackbarData: (data: { text: string; severity: string; isOpen: boolean }) => void;
}

const NODE_HORIZONTAL_OFFSET = 200;

export const useCsvDrop = ({ addNewNode, updateSnackbarData }: UseCsvDropParams) => {
  const { t } = useTranslation();
  const [csvDialogState, setCsvDialogState] = useState<CsvDialogState>({
    open: false,
    columns: [],
    file: null,
    dropPosition: { x: 0, y: 0 },
  });

  const createNodesFromColumns = useCallback(
    (file: File, selectedColumns: string[], dropPosition: { x: number; y: number }): Promise<void> => {
      return new Promise((resolve) => {
        Papa.parse<Record<string, string>>(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const maxOptions = getListSelectorIteratorMaxOptions();
            let hasTruncation = false;

            selectedColumns.forEach((column, index) => {
              let columnValues = results.data
                .map((row) => row[column])
                .filter((val) => val !== null && val !== undefined && val !== '');

              if (columnValues.length > maxOptions) {
                columnValues = columnValues.slice(0, maxOptions);
                hasTruncation = true;
              }

              addNewNode({
                action: {
                  type: 'list_selector',
                  id: TEXT_ITERATOR_ID,
                  initialData: { list: columnValues, name: column },
                },
                dropX: dropPosition.x + index * NODE_HORIZONTAL_OFFSET,
                dropY: dropPosition.y,
              });
            });

            if (hasTruncation) {
              updateSnackbarData({
                text: t(I18N_KEYS.CSV_COLUMN_DIALOG.WARNING_TRUNCATED, {
                  max: maxOptions,
                }),
                severity: 'warning',
                isOpen: true,
              });
            }

            resolve();
          },
          error: (err) => {
            log.error('Failed to parse CSV for column extraction', err);
            updateSnackbarData({
              text: t(I18N_KEYS.CSV_COLUMN_DIALOG.ERROR_PARSE),
              severity: 'error',
              isOpen: true,
            });
            resolve();
          },
        });
      });
    },
    [addNewNode, t, updateSnackbarData],
  );

  const handleCsvFile = useCallback(
    (file: File, dropX: number, dropY: number) => {
      Papa.parse<Record<string, string>>(file, {
        header: true,
        preview: 10,
        skipEmptyLines: true,
        complete: (results) => {
          const columns = results.meta.fields || [];
          if (columns.length === 0) {
            log.warn(`CSV file has no columns: ${file.name}`);
            updateSnackbarData({
              text: t(I18N_KEYS.CSV_COLUMN_DIALOG.ERROR_EMPTY),
              severity: 'error',
              isOpen: true,
            });
          } else if (columns.length === 1) {
            // Single column - create iterator directly without dialog
            void createNodesFromColumns(file, columns, { x: dropX, y: dropY });
          } else {
            // Multiple columns - show dialog for selection
            setCsvDialogState({
              open: true,
              columns,
              file,
              dropPosition: { x: dropX, y: dropY },
            });
          }
        },
        error: (err) => {
          log.error(`Failed to parse CSV: ${file.name}`, err);
          updateSnackbarData({
            text: t(I18N_KEYS.CSV_COLUMN_DIALOG.ERROR_PARSE),
            severity: 'error',
            isOpen: true,
          });
        },
      });
    },
    [t, updateSnackbarData, createNodesFromColumns],
  );

  const handleCsvColumnsSelect = useCallback(
    async (selectedColumns: string[]): Promise<void> => {
      const { file, dropPosition } = csvDialogState;
      if (!file || selectedColumns.length === 0) return;

      await createNodesFromColumns(file, selectedColumns, dropPosition);
      setCsvDialogState((prev) => ({ ...prev, open: false }));
    },
    [csvDialogState, createNodesFromColumns],
  );

  const handleCsvDialogClose = useCallback(() => {
    setCsvDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  const isCsvFile = useCallback((file: File) => {
    return file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
  }, []);

  return {
    csvDialogState,
    handleCsvFile,
    handleCsvColumnsSelect,
    handleCsvDialogClose,
    isCsvFile,
  };
};
